import apiCaller from "./axios.service";
import type { ResponseData } from "../model/Responses";

const baseUrl = "/v1/private/hybridPlayer";

export interface HybridRegisterRequestOtpPayload {
	player_phone_no: string;
}

export const hybridRegisterRequestOtp = (
	payload: HybridRegisterRequestOtpPayload
) => {
	return apiCaller(`${baseUrl}/hybridRegisterRequestOtp`, payload);
};

export interface HybridRegisterCreateAccountPayload {
	hybrid_player_name: string;
	player_phone_no: string;
	otp_code: string;
	player_password: string;
	referral_username?: string;
	region_id?: number;
}

export const hybridRegisterCreateAccount = (
	payload: HybridRegisterCreateAccountPayload
) => {
	return apiCaller(`${baseUrl}/hybridRegisterCreateAccount`, payload);
};

export interface HybridVerifyPhoneNumberPayload {
	hybrid_player_username: string;
	player_phone_no: string;
	otp_code: string;
}

export const hybridVerifyPhoneNumber = (
	payload: HybridVerifyPhoneNumberPayload
) => {
	return apiCaller(`${baseUrl}/hybridVerifyPhoneNumber`, payload);
};

export interface HybridAddBankAccountPayload {
	b2c_withdraw_bank_setting_id: number;
	bank_code: string;
	bank_name: string;
	account_number: string;
	account_holder_name: string;
}

export const hybridAddBankAccount = (payload: HybridAddBankAccountPayload) => {
	return apiCaller(`${baseUrl}/hybridAddBankAccount`, payload);
};

export interface HybridRemoveBankAccountPayload {
	hybrid_player_bank_account_id: number;
}

export const hybridRemoveBankAccount = (
	payload: HybridRemoveBankAccountPayload
) => {
	return apiCaller(`${baseUrl}/hybridRemoveBankAccount`, payload);
};

export interface HybridChangePasswordPayload {
	old_password: string;
	new_password: string;
}

export const hybridChangePassword = (payload: HybridChangePasswordPayload) => {
	return apiCaller(`${baseUrl}/hybridChangePassword`, {
		old_password: payload.old_password.trim(),
		new_password: payload.new_password.trim(),
	});
};

export interface HybridGenerate2FAResult {
	otpauth_url?: string;
	base32_secret?: string;
}

export const hybridGenerate2FA = () => {
	return apiCaller(`${baseUrl}/hybridGenerate2FA`, {});
};

export interface HybridEnable2FAPayload {
	otp_code: string;
}

export const hybridEnable2FA = (payload: HybridEnable2FAPayload) => {
	return apiCaller(`${baseUrl}/hybridEnable2FA`, {
		otp_code: String(payload.otp_code ?? "").trim(),
	});
};

export interface HybridDisable2FAPayload {
	otp_code?: string;
}

export const hybridDisable2FA = (payload?: HybridDisable2FAPayload) => {
	return apiCaller(`${baseUrl}/hybridDisable2FA`, payload || {});
};

export interface HybridBindEmailPayload {
	hybrid_player_email: string;
}

export const hybridBindEmail = (payload: HybridBindEmailPayload) => {
	return apiCaller(`${baseUrl}/hybridBindEmail`, {
		hybrid_player_email: String(payload.hybrid_player_email ?? "")
			.trim()
			.toLowerCase(),
	});
};

export interface HybridVerifyEmailPayload {
	hybrid_player_email: string;
	tac: string;
}

export const hybridVerifyEmail = (payload: HybridVerifyEmailPayload) => {
	return apiCaller(`${baseUrl}/hybridVerifyEmail`, {
		hybrid_player_email: String(payload.hybrid_player_email ?? "")
			.trim()
			.toLowerCase(),
		tac: String(payload.tac ?? "").trim(),
	});
};

export const hybridRequestChangeEmail = () => {
	return apiCaller(`${baseUrl}/hybridRequestChangeEmail`, {});
};

export interface HybridVerifyChangeEmailPayload {
	hybrid_player_email: string;
	tac: string;
}

export const hybridVerifyChangeEmail = (
	payload: HybridVerifyChangeEmailPayload
) => {
	return apiCaller(`${baseUrl}/hybridVerifyChangeEmail`, {
		hybrid_player_email: String(payload.hybrid_player_email ?? "")
			.trim()
			.toLowerCase(),
		tac: String(payload.tac ?? "").trim(),
	});
};

export const hybridGetWithdrawBankSetting = () => {
	return apiCaller(`${baseUrl}/hybridGetWithdrawBankSetting`, {});
};

export interface HybridWithdrawBankSettingRecord {
	b2c_withdraw_bank_setting_id?: number;
	b2c_withdraw_bank_setting_name?: string;
	b2c_withdraw_bank_setting_code?: string;
	b2c_withdraw_bank_setting_fpay_id?: string | number;
}

/** Hybrid player's saved bank accounts from `POST /v1/private/hybridPlayer/hybridGetPlayerBankAccount`. */
export const HYBRID_PLAYER_BANK_ACCOUNT_STATUS = {
	INACTIVE: 0,
	ACTIVE: 1,
	PENDING_APPROVAL: 2,
} as const;

export interface HybridGetPlayerBankAccountPayload {
	hybrid_player_bank_account_status?: number;
}

export const hybridGetPlayerBankAccount = (
	payload?: HybridGetPlayerBankAccountPayload
) => {
	const body: Record<string, number> = {};
	if (
		payload?.hybrid_player_bank_account_status != null &&
		Number.isFinite(payload.hybrid_player_bank_account_status)
	) {
		body.hybrid_player_bank_account_status =
			payload.hybrid_player_bank_account_status;
	}
	return apiCaller(`${baseUrl}/hybridGetPlayerBankAccount`, body);
};

/** Company receiver account for hybrid deposit (picked server-side by amount / limits). */
export interface HybridReceiverBankAccountRow {
	bank_account_setting_id?: number;
	bank_setting_id?: number;
	bank_name?: string;
	bank_code?: string;
	receiver_bank_name: string;
	receiver_bank_acc_no: string;
	receiver_bank_acc_name: string;
	ordering?: number;
	bank_type?: number;
}

export const hybridGetReceiverBankAccount = (payload?: { amount?: number }) => {
	const body: Record<string, number> = {};
	if (
		payload?.amount != null &&
		Number.isFinite(payload.amount) &&
		payload.amount > 0
	) {
		body.amount = payload.amount;
	}
	return apiCaller(`${baseUrl}/hybridGetReceiverBankAccount`, body);
};

export interface HybridGetDepositWithdrawMinMaxPayload {
	amount?: number;
	type?: "deposit" | "withdraw";
	/** Bank transfer on `/deposit` uses limit-group min/max from the API. */
	payment_method?: "bank_transfer";
	method_type_id?: number;
}

export interface HybridDepositWithdrawMinMaxData {
	type?: string;
	payment_method?: string;
	amount?: number;
	min?: number | null;
	max?: number | null;
}

export const hybridGetDepositWithdrawMinMax = (
	payload: HybridGetDepositWithdrawMinMaxPayload
) => {
	const body: Record<string, string | number> = {};
	if (
		payload.amount != null &&
		Number.isFinite(payload.amount) &&
		payload.amount > 0
	) {
		body.amount = payload.amount;
	}
	if (payload.type) {
		body.type = payload.type;
	}
	if (payload.payment_method) {
		body.payment_method = payload.payment_method;
	}
	if (
		payload.method_type_id != null &&
		Number.isFinite(payload.method_type_id)
	) {
		body.method_type_id = payload.method_type_id;
	}
	return apiCaller(`${baseUrl}/hybridGetDepositWithdrawMinMax`, body);
};

export interface HybridDepositChannel {
	gateway_id: number;
	gateway_code: string;
	gateway_name: string;
	method_type: string;
	method_type_id: number;
	min_amount: number;
	max_amount: number;
	/** Set when the player must pick their bank before paying (JGO FPX). */
	banks?: { code: string; name: string }[];
}

/**
 * DB-driven catalog of gateway-backed deposit channels (FPAY x3, 1MPAY) —
 * the backend equivalent of BANK_TRANSFER/RELOAD_PIN, which stay as static
 * entries in depositShared.ts since they aren't gateway-driven.
 */
export const hybridGetDepositChannels = () => {
	return apiCaller(`${baseUrl}/hybridGetDepositChannels`, {});
};

/** STS for S3 upload (hybrid players). Returns camelCase credentials + bucket/region/endPoint. */
export const hybridGenerateSTSToken = () => {
	return apiCaller(`${baseUrl}/hybridGenerateSTSToken`, {});
};

/** Matches user_api METHOD_TYPE_MAP — FPay online banking */
export const HYBRID_DEPOSIT_METHOD_FPAY = 31;

/** Matches user_api METHOD_TYPE_MAP — FPay e-wallet */
export const HYBRID_DEPOSIT_METHOD_FPAY_EWALLET = 32;

/** Matches user_api METHOD_TYPE_MAP — FPay DuitNow (QR) */
export const HYBRID_DEPOSIT_METHOD_FPAY_DUITNOW = 33;

/** Matches user_api METHOD_TYPE_MAP — 1MPAY (`hybridPlayerSubmitDepositOnempay`) */
export const HYBRID_DEPOSIT_METHOD_ONEMPAY = 34;

/** Matches user_api METHOD_TYPE_MAP for bank transfer deposits */
export const HYBRID_DEPOSIT_METHOD_BANK_TRANSFER = 35;

/** Matches user_api METHOD_TYPE_MAP — RapidPays online banking (`hybridPlayerSubmitDepositGateway`) */
export const HYBRID_DEPOSIT_METHOD_RAPIDPAYS_ONLINE_BANKING = 37;

/** Matches user_api METHOD_TYPE_MAP — RapidPays DuitNow QR */
export const HYBRID_DEPOSIT_METHOD_RAPIDPAYS_DUITNOW = 38;

/** Matches user_api METHOD_TYPE_MAP — JGO FPX (the original JGO channel) */
export const HYBRID_DEPOSIT_METHOD_JGO = 39;

/** Matches user_api METHOD_TYPE_MAP — JGO e-wallet (TNG) */
export const HYBRID_DEPOSIT_METHOD_JGO_EWALLET = 43;

/** Matches user_api METHOD_TYPE_MAP — JGO DuitNow QR */
export const HYBRID_DEPOSIT_METHOD_JGO_DUITNOW = 44;

/** Matches user_api METHOD_TYPE_MAP — SeaPay online banking */
export const HYBRID_DEPOSIT_METHOD_SEAPAY_ONLINE_BANKING = 40;

/** Matches user_api METHOD_TYPE_MAP — SeaPay e-wallet */
export const HYBRID_DEPOSIT_METHOD_SEAPAY_EWALLET = 41;

/** Matches user_api METHOD_TYPE_MAP — SeaPay DuitNow QR */
export const HYBRID_DEPOSIT_METHOD_SEAPAY_DUITNOW = 42;

/**
 * Matches `netplay_user_api` `HybridPlayerService.hybridPlayerSubmitDeposit` (`req.body`).
 * Bank transfer (`method_type_id` 35): requires `uploaded_receipt` plus receiver fields;
 * `receiver_bank_name` must match an active B2C row’s `b2c_bank_setting_name`.
 */
export interface HybridPlayerSubmitDepositPayload {
	amount: number;
	method_type_id: number;
	/** Public URL or data URL string stored on the deposit record. */
	uploaded_receipt: string;
	receiver_bank_name: string;
	receiver_bank_acc_no: string;
	receiver_bank_acc_name: string;
	remark?: string;
	player_bonus_id?: number;
	/** Bonus package selected on deposit (no prior claim). */
	bonus_config_id?: number;
	/** Optional; current user_api handler does not use this but forwards are harmless. */
	hybrid_player_bank_account_id?: number | null;
}

export const hybridPlayerSubmitDeposit = (
	payload: HybridPlayerSubmitDepositPayload
) => {
	const body: Record<string, string | number> = {
		amount: payload.amount,
		method_type_id: payload.method_type_id,
		uploaded_receipt: payload.uploaded_receipt,
		receiver_bank_name: payload.receiver_bank_name,
		receiver_bank_acc_no: payload.receiver_bank_acc_no,
		receiver_bank_acc_name: payload.receiver_bank_acc_name,
	};
	if (payload.remark) {
		body.remark = payload.remark;
	}
	if (
		payload.bonus_config_id != null &&
		Number.isFinite(Number(payload.bonus_config_id)) &&
		Number(payload.bonus_config_id) > 0
	) {
		body.bonus_config_id = Number(payload.bonus_config_id);
	} else if (
		payload.player_bonus_id != null &&
		Number.isFinite(Number(payload.player_bonus_id)) &&
		Number(payload.player_bonus_id) > 0
	) {
		body.player_bonus_id = Number(payload.player_bonus_id);
	}
	if (
		payload.hybrid_player_bank_account_id != null &&
		payload.hybrid_player_bank_account_id !== undefined
	) {
		body.hybrid_player_bank_account_id = payload.hybrid_player_bank_account_id;
	}
	return apiCaller(`${baseUrl}/hybridPlayerSubmitDeposit`, body, "POST", {
		timeout: 120000,
	});
};

/** Matches `netplay_user_api` `HybridPlayerService.hybridPlayerSubmitDepositFPay`. */
export interface HybridPlayerSubmitDepositFPayPayload {
	amount: number;
	method_type_id: number;
	redirect_url?: string;
	currency?: string;
	remark?: string;
	player_bonus_id?: number;
	bonus_config_id?: number;
}

export interface HybridPlayerSubmitDepositFPayResponse {
	payment_url?: string;
	hdw_trans_id?: number;
	tableKey?: string;
	orderid?: string;
}

export const hybridPlayerSubmitDepositFPay = (
	payload: HybridPlayerSubmitDepositFPayPayload
) => {
	const body: Record<string, string | number> = {
		amount: payload.amount,
		method_type_id: payload.method_type_id,
	};
	if (payload.redirect_url?.trim()) {
		body.redirect_url = payload.redirect_url.trim();
	}
	if (payload.currency?.trim()) {
		body.currency = payload.currency.trim();
	}
	if (payload.remark?.trim()) {
		body.remark = payload.remark.trim();
	}
	if (
		payload.bonus_config_id != null &&
		Number.isFinite(Number(payload.bonus_config_id)) &&
		Number(payload.bonus_config_id) > 0
	) {
		body.bonus_config_id = Number(payload.bonus_config_id);
	} else if (
		payload.player_bonus_id != null &&
		Number.isFinite(Number(payload.player_bonus_id)) &&
		Number(payload.player_bonus_id) > 0
	) {
		body.player_bonus_id = Number(payload.player_bonus_id);
	}
	return apiCaller(`${baseUrl}/hybridPlayerSubmitDepositFPay`, body, "POST", {
		timeout: 120000,
	});
};

/** Matches `netplay_user_api` `HybridPlayerService.hybridPlayerSubmitDepositOnempay`. */
export interface HybridPlayerSubmitDepositOnempayPayload {
	amount: number;
	method_type_id: number;
	currency?: string;
	player_bonus_id?: number;
	bonus_config_id?: number;
}

export const hybridPlayerSubmitDepositOnempay = (
	payload: HybridPlayerSubmitDepositOnempayPayload
) => {
	const body: Record<string, string | number> = {
		amount: payload.amount,
		method_type_id: payload.method_type_id,
	};
	if (payload.currency?.trim()) {
		body.currency = payload.currency.trim();
	}
	if (
		payload.bonus_config_id != null &&
		Number.isFinite(Number(payload.bonus_config_id)) &&
		Number(payload.bonus_config_id) > 0
	) {
		body.bonus_config_id = Number(payload.bonus_config_id);
	} else if (
		payload.player_bonus_id != null &&
		Number.isFinite(Number(payload.player_bonus_id)) &&
		Number(payload.player_bonus_id) > 0
	) {
		body.player_bonus_id = Number(payload.player_bonus_id);
	}
	return apiCaller(
		`${baseUrl}/hybridPlayerSubmitDepositOnempay`,
		body,
		"POST",
		{
			timeout: 120000,
		}
	);
};

/**
 * Matches `netplay_user_api` `HybridPlayerService.hybridPlayerSubmitDepositGateway`
 * (RAPIDPAYS / JGO / SEAPAY, `method_type_id` 37-44). Returns `{ payment_url }`.
 */
export interface HybridPlayerSubmitDepositGatewayPayload {
	amount: number;
	method_type_id: number;
	redirect_url?: string;
	player_bonus_id?: number;
	bonus_config_id?: number;
	/** The player's bank, for channels that need it (JGO FPX). */
	bank_code?: string;
}

export const hybridPlayerSubmitDepositGateway = (
	payload: HybridPlayerSubmitDepositGatewayPayload
) => {
	const body: Record<string, string | number> = {
		amount: payload.amount,
		method_type_id: payload.method_type_id,
	};
	if (payload.redirect_url?.trim()) {
		body.redirect_url = payload.redirect_url.trim();
	}
	if (payload.bank_code?.trim()) {
		body.bank_code = payload.bank_code.trim();
	}
	if (
		payload.bonus_config_id != null &&
		Number.isFinite(Number(payload.bonus_config_id)) &&
		Number(payload.bonus_config_id) > 0
	) {
		body.bonus_config_id = Number(payload.bonus_config_id);
	} else if (
		payload.player_bonus_id != null &&
		Number.isFinite(Number(payload.player_bonus_id)) &&
		Number(payload.player_bonus_id) > 0
	) {
		body.player_bonus_id = Number(payload.player_bonus_id);
	}
	return apiCaller(
		`${baseUrl}/hybridPlayerSubmitDepositGateway`,
		body,
		"POST",
		{
			timeout: 120000,
		}
	);
};

export const hybridGetProfile = () => {
	return apiCaller(`${baseUrl}/hybridGetProfile`);
};

export interface HybridPlayerMoreSettings {
	allow_add_bank_account?: boolean;
	max_bank_accounts?: number;
	daily_max_withdrawal?: number;
	lifetime_max_withdrawal?: number;
	per_transaction_max_withdrawal?: number;
	per_transaction_min_deposit?: number;
	p2p_transfer_cash_point_enabled?: boolean;
	p2p_transfer_np_point_enabled?: boolean;
	submit_withdraw_enabled?: boolean;
	redeem_gift_np_point_enabled?: boolean;
	withdrawl_fee_setting?: number;
	daily_max_rebate_earned?: number;
}

export const parseHybridPlayerMoreSettings = (
	data: unknown
): HybridPlayerMoreSettings | null => {
	const row = Array.isArray(data) ? data[0] : data;
	const settings = (row as { hybrid_player_more_settings?: unknown } | null)
		?.hybrid_player_more_settings;
	if (!settings || typeof settings !== "object") {
		return null;
	}
	return settings as HybridPlayerMoreSettings;
};

export interface HybridGetUserBalanceData {
	balance?: number;
	hybrid_player_bonus_wallet?: number;
	hybrid_player_bonus_mode?: string;
	hybrid_player_bonus_release_pending_transfer_out?: boolean | string | number;
	hybrid_player_bonus_pending_cs_release?: boolean | string | number;
}

const parseBalanceFlag = (value: unknown): boolean =>
	value === true ||
	value === 1 ||
	String(value ?? "")
		.trim()
		.toLowerCase() === "true";

/** Normalize `hybridGetUserBalance` payload (object or legacy number). */
export const parseHybridUserBalance = (
	data: HybridGetUserBalanceData | number | null | undefined
): {
	balance: number;
	bonusWallet: number;
	bonusMode: boolean;
	releasePendingTransferOut: boolean;
	pendingCsRelease: boolean;
} => {
	if (typeof data === "number") {
		return {
			balance: Number.isFinite(data) ? data : 0,
			bonusWallet: 0,
			bonusMode: false,
			releasePendingTransferOut: false,
			pendingCsRelease: false,
		};
	}
	const balance = Number(data?.balance ?? 0);
	const bonusWallet = Number(data?.hybrid_player_bonus_wallet ?? 0);
	const bonusMode =
		String(data?.hybrid_player_bonus_mode ?? "FALSE")
			.trim()
			.toUpperCase() === "TRUE";
	return {
		balance: Number.isFinite(balance) ? balance : 0,
		bonusWallet: Number.isFinite(bonusWallet) ? bonusWallet : 0,
		bonusMode,
		releasePendingTransferOut: parseBalanceFlag(
			data?.hybrid_player_bonus_release_pending_transfer_out
		),
		pendingCsRelease: parseBalanceFlag(
			data?.hybrid_player_bonus_pending_cs_release
		),
	};
};

export const hybridGetUserBalance = () => {
	return apiCaller(`${baseUrl}/hybridGetUserBalance`);
};

export interface HybridTurnoverStatus {
	total_required_valid_bet: number;
	achieved_valid_bet: number;
	remaining_valid_bet: number;
	player_turnover_requirement_id?: number | null;
	player_turnover_requirement_status?: number | null;
	player_turnover_requirement_status_label?: string | null;
	has_ongoing_turnover_requirement?: boolean;
}

export const hybridGetTurnoverStatus = () => {
	return apiCaller(`${baseUrl}/hybridGetTurnoverStatus`, {});
};

export const hybridGetDepositWithdrawHistory = () => {
	return apiCaller(`${baseUrl}/hybridGetDepositWithdrawHistory`);
};

/** Row from `hybrid_deposit_withdraw_transaction_log_{YYYYMM}` (deposit action id 11). */
export interface HybridDepositHistoryRecord {
	hdw_trans_amount?: string | number;
	hdw_trans_method_type?: string;
	hdw_trans_created_dt?: string;
	hdw_trans_status_type?: string;
}

export interface HybridGetDepositHistoryData {
	table_key?: string;
	records?: HybridDepositHistoryRecord[];
}

export const hybridGetDepositHistory = (payload?: { table_key?: string }) => {
	const body: Record<string, string> = {};
	if (payload?.table_key?.trim()) {
		body.table_key = payload.table_key.trim();
	}
	return apiCaller(`${baseUrl}/hybridGetDepositHistory`, body);
};

/** Same row shape as deposit history (`hybridGetWithdrawHistory`). */
export type HybridWithdrawHistoryRecord = HybridDepositHistoryRecord;

export const hybridGetWithdrawHistory = (payload?: { table_key?: string }) => {
	const body: Record<string, string> = {};
	if (payload?.table_key?.trim()) {
		body.table_key = payload.table_key.trim();
	}
	return apiCaller(`${baseUrl}/hybridGetWithdrawHistory`, body);
};

/** Row from `hybrid_player_daily_rebate_source_{YYYYMM}` (per downline / self). */
export interface HybridRebateHistoryRecord {
	stat_date?: string;
	source?: string;
	amount?: number | string;
	generation?: number;
	source_hybrid_player_id?: number;
}

export interface HybridRebateHistorySummary {
	self_amount?: number;
	downline_amount?: number;
	total_amount?: number;
	record_count?: number;
}

export interface HybridGetRebateHistoryData {
	table_key?: string;
	summary?: HybridRebateHistorySummary;
	records?: HybridRebateHistoryRecord[];
}

export interface HybridGetRebateHistoryPayload {
	/** Single-day shortcut (YYYY-MM-DD). Sets both start_date and end_date on API. */
	date?: string;
	table_key?: string;
	start_date?: string;
	end_date?: string;
}

export const EMPTY_HYBRID_REBATE_SUMMARY: HybridRebateHistorySummary = {
	self_amount: 0,
	downline_amount: 0,
	total_amount: 0,
	record_count: 0,
};

export const hybridGetRebateHistory = (
	payload?: HybridGetRebateHistoryPayload
) => {
	const body: Record<string, string> = {};
	if (payload?.table_key?.trim()) {
		body.table_key = payload.table_key.trim();
	}
	if (payload?.date?.trim()) {
		body.date = payload.date.trim();
	}
	if (payload?.start_date?.trim()) {
		body.start_date = payload.start_date.trim();
	}
	if (payload?.end_date?.trim()) {
		body.end_date = payload.end_date.trim();
	}
	return apiCaller(`${baseUrl}/hybridGetRebateHistory`, body);
};

export const HYBRID_CLAIM_CATEGORY = {
	REWARDS: "REWARDS",
	RELOAD_PIN: "RELOAD_PIN",
	VOUCHER: "VOUCHER",
} as const;

export type HybridClaimCategory =
	(typeof HYBRID_CLAIM_CATEGORY)[keyof typeof HYBRID_CLAIM_CATEGORY];

export interface HybridClaimCatalogRecord {
	catalog_id?: number;
	region_id?: number | null;
	category?: string;
	name?: string;
	description?: string | null;
	image_url?: string | null;
	np_points_cost?: number | string;
	reward_type?: string;
	reward_value?: number | string | null;
	sort_order?: number;
	stock_qty?: number | null;
	redeemed_qty?: number;
}

export interface HybridClaimHistoryRecord {
	redemption_id?: number;
	table_key?: string | null;
	catalog_id?: number;
	category?: string;
	item_name?: string;
	reward_type?: string;
	reward_value?: number | string | null;
	np_points_cost?: number | string;
	wallet_credit_amount?: number | string | null;
	status?: string;
	remark?: string | null;
	fail_reason?: string | null;
	created_datetime?: string;
	completed_datetime?: string | null;
	history_source?: string;
	reload_pin?: {
		pin_id?: number;
		pin_code?: string;
		status?: string;
		issued_hybrid_player_id?: number | null;
		issued_by_username?: string | null;
		redeemed_hybrid_player_id?: number | null;
		redeemed_by_username?: string | null;
		redeemed_datetime?: string | null;
		expire_datetime?: string | null;
	} | null;
}

export interface HybridGetClaimCatalogPayload {
	category?: HybridClaimCategory | string;
}

export interface HybridSubmitClaimPayload {
	catalog_id: number;
	request_ref?: string;
	/** Required for Rewards / physical gifts */
	recipient_name?: string;
	contact_info?: string;
	delivery_address?: string;
	delivery_remark?: string;
}

export interface HybridGetClaimHistoryPayload {
	table_key?: string;
	category?: HybridClaimCategory | string;
	date?: string;
	start_date?: string;
	end_date?: string;
}

export const hybridGetClaimCatalog = (
	payload?: HybridGetClaimCatalogPayload
) => {
	const body: Record<string, string> = {};
	if (payload?.category?.trim()) {
		body.category = payload.category.trim().toUpperCase();
	}
	return apiCaller(`${baseUrl}/hybridGetClaimCatalog`, body);
};

export const hybridSubmitClaim = (payload: HybridSubmitClaimPayload) => {
	const body: Record<string, string | number> = {
		catalog_id: payload.catalog_id,
	};
	if (payload.request_ref?.trim()) {
		body.request_ref = payload.request_ref.trim();
	}
	if (payload.recipient_name?.trim()) {
		body.recipient_name = payload.recipient_name.trim();
	}
	if (payload.contact_info?.trim()) {
		body.contact_info = payload.contact_info.trim();
	}
	if (payload.delivery_address?.trim()) {
		body.delivery_address = payload.delivery_address.trim();
	}
	if (payload.delivery_remark?.trim()) {
		body.delivery_remark = payload.delivery_remark.trim();
	}
	return apiCaller(`${baseUrl}/hybridSubmitClaim`, body);
};

export const hybridGetClaimHistory = (
	payload?: HybridGetClaimHistoryPayload
) => {
	const body: Record<string, string> = {};
	if (payload?.table_key?.trim()) {
		body.table_key = payload.table_key.trim();
	}
	if (payload?.category?.trim()) {
		body.category = payload.category.trim().toUpperCase();
	}
	if (payload?.date?.trim()) {
		body.date = payload.date.trim();
	}
	if (payload?.start_date?.trim()) {
		body.start_date = payload.start_date.trim();
	}
	if (payload?.end_date?.trim()) {
		body.end_date = payload.end_date.trim();
	}
	return apiCaller(`${baseUrl}/hybridGetClaimHistory`, body);
};

export const HYBRID_PLAYER_BONUS_STATUS = {
	CLAIMED: "CLAIMED",
	PENDING_DEPOSIT: "PENDING_DEPOSIT",
	ACTIVE: "ACTIVE",
	PENDING_CS_RELEASE: "PENDING_CS_RELEASE",
	COMPLETED: "COMPLETED",
	FORFEITED: "FORFEITED",
	CANCELLED: "CANCELLED",
	EXPIRED: "EXPIRED",
} as const;

export type HybridPlayerBonusStatus =
	(typeof HYBRID_PLAYER_BONUS_STATUS)[keyof typeof HYBRID_PLAYER_BONUS_STATUS];

export interface HybridBonusPgMethod {
	type_id: number;
	type: string;
}

export interface HybridBonusProvider {
	provider_id: number;
	provider_name: string;
	categories?: string[];
}

export interface HybridBonusCatalogRecord {
	config_id: number;
	region_id?: number | null;
	name: string;
	description: string | null;
	bonus_type: string;
	bonus_value: number;
	bonus_max_amount: number | null;
	min_deposit: number;
	rollover_multiplier: number;
	np_points_cost: number;
	max_claims_per_player: number | null;
	player_claim_count: number;
	can_claim: boolean;
	pg_methods: HybridBonusPgMethod[];
	providers: HybridBonusProvider[];
}

export interface HybridPlayerBonusRecord {
	player_bonus_id: number;
	config_id: number;
	status: string;
	np_points_deducted: number;
	bonus_amount: number;
	rollover_multiplier: number;
	deposit_amount: number | null;
	deposit_trans_id: number | null;
	total_credit_entered: number | null;
	turnover_requirement_id: number | null;
	activated_datetime: string | null;
	released_datetime: string | null;
	created_datetime: string;
	config: {
		name: string;
		description: string | null;
		bonus_type: string;
		bonus_value: number;
		bonus_max_amount: number | null;
		min_deposit: number;
		rollover_multiplier: number;
		pg_methods: HybridBonusPgMethod[];
		providers: HybridBonusProvider[];
	} | null;
}

export interface HybridGetMyBonusesPayload {
	status?: HybridPlayerBonusStatus | "ALL" | string;
}

export const hybridGetBonusCatalog = () => {
	return apiCaller(`${baseUrl}/hybridGetBonusCatalog`, {});
};

export const hybridGetMyBonuses = (payload?: HybridGetMyBonusesPayload) => {
	const body: Record<string, string> = {};
	if (payload?.status?.trim()) {
		body.status = payload.status.trim().toUpperCase();
	}
	return apiCaller(`${baseUrl}/hybridGetMyBonuses`, body);
};

export const hybridClaimBonus = (configId: number) => {
	return apiCaller(`${baseUrl}/hybridClaimBonus`, { config_id: configId });
};

export interface HybridReloadPinRecord {
	pin_id?: number;
	pin_code?: string;
	catalog_id?: number;
	credit_amount?: number | string;
	status?: string;
	issued_redemption_id?: number | null;
	issued_table_key?: string | null;
	issued_hybrid_player_id?: number | null;
	issued_by_username?: string | null;
	expire_datetime?: string | null;
	redeemed_hybrid_player_id?: number | null;
	redeemed_by_username?: string | null;
	redeemed_datetime?: string | null;
	created_datetime?: string;
}

export interface HybridRedeemReloadPinPayload {
	pin_code: string;
}

export const hybridGetMyReloadPins = () => {
	return apiCaller(`${baseUrl}/hybridGetMyReloadPins`, {});
};

export const hybridRedeemReloadPin = (
	payload: HybridRedeemReloadPinPayload
) => {
	return apiCaller(`${baseUrl}/hybridRedeemReloadPin`, {
		pin_code: String(payload.pin_code || "")
			.trim()
			.toUpperCase(),
	});
};

export interface HybridPlayerBankAccountRecord {
	hybrid_player_bank_account_id?: number;
	hybrid_player_bank_account_bank_code?: string;
	hybrid_player_bank_account_bank_name?: string;
	hybrid_player_bank_account_account_number?: string;
	hybrid_player_bank_account_account_holder_name?: string;
	hybrid_player_bank_account_status?: number;
}

export interface HybridPlayerSubmitWithdrawPayload {
	amount: number;
	hybrid_player_bank_account_id: number;
	remark?: string;
}

export const hybridPlayerSubmitWithdraw = (
	payload: HybridPlayerSubmitWithdrawPayload
) => {
	const body: Record<string, string | number> = {
		amount: payload.amount,
		hybrid_player_bank_account_id: payload.hybrid_player_bank_account_id,
	};
	if (payload.remark?.trim()) {
		body.remark = payload.remark.trim();
	}
	return apiCaller(`${baseUrl}/hybridPlayerSubmitWithdraw`, body, "POST", {
		timeout: 120000,
	});
};

/** Matches `netplay_user_api` `HybridPlayerService.hybridPlayerTransferCreditToHybridPlayer`. */
export interface HybridPlayerTransferCreditPayload {
	amount: number;
	hybrid_player_username?: string;
	hybrid_player_phone_no?: string;
}

export interface HybridPlayerTransferCreditResponse {
	transactionID?: string;
	amount?: number;
	balance?: number;
}

export const hybridPlayerTransferCreditToHybridPlayer = (
	payload: HybridPlayerTransferCreditPayload
) => {
	const body: Record<string, string | number> = {
		amount: payload.amount,
	};
	const username = payload.hybrid_player_username?.trim();
	const phoneNo = payload.hybrid_player_phone_no?.trim();
	if (username) {
		body.hybrid_player_username = username;
	}
	if (phoneNo) {
		body.hybrid_player_phone_no = phoneNo;
	}
	return apiCaller(
		`${baseUrl}/hybridPlayerTransferCreditToHybridPlayer`,
		body,
		"POST",
		{ timeout: 120000 }
	);
};

/** Row from `hybrid_player_transfer_log_{YYYYMM}` (`hybridGetTransferCreditHistory`). */
export interface HybridTransferCreditHistoryRecord {
	trans_id?: number;
	trans_created_dt?: string;
	trans_sender_hybrid_player_username?: string;
	trans_recipient_hybrid_player_username?: string;
	counterparty_hybrid_player_phone_no?: string | null;
	trans_amount?: string | number;
	trans_status_type?: string;
	transfer_direction?: "SENT" | "RECEIVED";
}

export interface HybridGetTransferCreditHistoryData {
	table_key?: string;
	records?: HybridTransferCreditHistoryRecord[];
}

export const hybridGetTransferCreditHistory = (payload?: {
	table_key?: string;
}) => {
	const body: Record<string, string> = {};
	if (payload?.table_key?.trim()) {
		body.table_key = payload.table_key.trim();
	}
	return apiCaller(`${baseUrl}/hybridGetTransferCreditHistory`, body);
};

export const hybridGetTransferAccount = async <T>(
	provider_id: number
): Promise<T> => {
	return apiCaller(`${baseUrl}/hybridGetTransferAccount`, {
		provider_id,
	});
};

/** One transfer (provider) account of the logged-in hybrid player. */
export interface HybridTransferWalletRow {
	provider_id: number;
	provider_name: string;
	player_id: number;
	transfer_player_user_id: string;
	wallet_balance: number;
	/** "live" = fetched from provider just now; "cached" = last synced amount */
	balance_source: "live" | "cached";
}

/** POST `/v1/private/hybridPlayer/hybridGetAllTransferAccounts` — all providers, no passwords. */
export const hybridGetAllTransferAccounts = async <T>(): Promise<T> => {
	return apiCaller(`${baseUrl}/hybridGetAllTransferAccounts`, {});
};

export type HybridGameLogSource = "ALL" | "SEAMLESS" | "TRANSFER";

export interface HybridGameLogRow {
	key: string;
	source: "SEAMLESS" | "TRANSFER";
	provider_id: number;
	provider_name: string;
	game_name: string | null;
	round_id: string | null;
	bet_datetime: string;
	bet_amount: number;
	win_amount: number;
	valid_bet: number;
	status: string | null;
}

export interface HybridGameLogTotals {
	count: number;
	bet_amount: number;
	win_amount: number;
	valid_bet: number;
}

export interface HybridGameLogResponseData {
	date: string;
	source: HybridGameLogSource;
	provider_id: number | null;
	current: number;
	pageSize: number;
	total: number;
	max_rows: number;
	list: HybridGameLogRow[];
	summary: {
		all: HybridGameLogTotals;
		seamless: HybridGameLogTotals;
		transfer: HybridGameLogTotals;
	};
	providers: { provider_id: number; provider_name: string }[];
	unavailable_sources: ("SEAMLESS" | "TRANSFER")[];
}

export interface HybridGetGameLogsPayload {
	/** YYYY-MM-DD, one day per request */
	date: string;
	source?: HybridGameLogSource;
	provider_id?: number | null;
	current?: number;
	pageSize?: number;
}

/** POST `/v1/private/hybridPlayer/hybridGetGameLogs` — seamless + transfer bets of the logged-in player. */
export const hybridGetGameLogs = async <T>(
	payload: HybridGetGameLogsPayload
): Promise<T> => {
	return apiCaller(`${baseUrl}/hybridGetGameLogs`, payload);
};

export const hybridCreateTransferAccount = async <T>(
	player_provider_id: number
): Promise<T> => {
	return apiCaller(`${baseUrl}/hybridCreateTransferAccount`, {
		player_provider_id,
	});
};

export const hybridDepositTransferAccount = async <T>({
	player_id,
	amount,
}: {
	player_id: number;
	amount: number;
}): Promise<T> => {
	return apiCaller(`${baseUrl}/hybridDepositTransferAccount`, {
		player_id,
		amount,
	});
};

export const hybridWithdrawTransferAccount = async <T>({
	player_id,
	amount,
}: {
	player_id: number;
	amount: number;
}): Promise<T> => {
	return apiCaller(`${baseUrl}/hybridWithdrawTransferAccount`, {
		player_id,
		amount,
	});
};

export const hybridGenerateTransferGameUrl = async <T>({
	player_id,
	language,
	game_list_game_id,
}: {
	player_id: number;
	language: string;
	game_list_game_id?: number;
}): Promise<T> => {
	return apiCaller(`${baseUrl}/hybridGenerateTransferGameUrl`, {
		player_id,
		language,
		game_list_game_id,
	});
};

export const hybridGenerateTransferWebsiteGameUrl = async <T>({
	player_id,
	language,
}: {
	player_id: number;
	language: string;
}): Promise<T> => {
	return apiCaller(`${baseUrl}/hybridGenerateTransferWebsiteGameUrl`, {
		player_id,
		language,
	});
};

export const hybridGenerateAppGame = async <T>({
	player_provider_id,
	player_id,
	language,
	isAndroid,
}: {
	player_provider_id: number;
	player_id: number;
	language: string;
	isAndroid: boolean;
}): Promise<T> => {
	return apiCaller(`${baseUrl}/hybridGenerateAppGame`, {
		player_provider_id,
		player_id,
		language,
		isAndroid,
	});
};

export const hybridGetProvider = async <T>(
	provider_category: string,
	provider_type: string
): Promise<T> => {
	return apiCaller(`${baseUrl}/hybridGetProvider`, {
		provider_category,
		provider_type,
	});
};

export const hybridGetGameCategoryList = async <T>(
	game_category_provider_id: number
): Promise<T> => {
	return apiCaller(`${baseUrl}/hybridGetGameCategoryList`, {
		game_category_provider_id,
	});
};

export const hybridGetGameList = async <T>({
	game_list_game_category_id,
	game_list_provider_id,
}: {
	game_list_game_category_id?: string;
	game_list_provider_id?: number;
}): Promise<T> => {
	return apiCaller(`${baseUrl}/hybridGetGameList`, {
		game_list_game_category_id,
		game_list_provider_id,
	});
};

export const hybridGetTransferGameList = async <T>({
	game_list_game_category_id,
	game_list_provider_id,
	player_id,
}: {
	game_list_game_category_id?: string;
	game_list_provider_id?: number;
	player_id?: number;
}): Promise<T> => {
	return apiCaller(`${baseUrl}/hybridGetTransferGameList`, {
		game_list_game_category_id,
		game_list_provider_id,
		player_id,
	});
};

export const hybridGenerateSeamlessGameUrl = async <T>(
	game_list_game_id: number,
	player_game_provider_id: number,
	selectedType: string
): Promise<T> => {
	return apiCaller(`${baseUrl}/hybridGenerateSeamlessGameUrl`, {
		game_list_game_id,
		player_game_provider_id,
		selectedType,
	});
};

/** Provider website lobby URL for hybrid player's linked seamless wallet. */
export const hybridGenerateSeamlessWebsiteGameUrl = async <T>(
	player_game_provider_id: number
): Promise<T> => {
	return apiCaller(`${baseUrl}/hybridGenerateSeamlessWebsiteGameUrl`, {
		player_game_provider_id,
	});
};

export const hybridGetLatestPlayerToken = async <T>(token: any): Promise<T> => {
	return apiCaller(`${baseUrl}/hybridGetLatestPlayerToken`, {
		token,
	});
};

/** Ticket statuses from hybrid player ticket APIs. */
export const HYBRID_TICKET_STATUS = {
	/** Waiting for admin handling. Player may close. */
	PENDING: "PENDING",
	/** Closed by admin after handling. */
	RESOLVED: "RESOLVED",
	/** Closed by the player. */
	CLOSED: "CLOSED",
} as const;

export type HybridTicketStatus =
	(typeof HYBRID_TICKET_STATUS)[keyof typeof HYBRID_TICKET_STATUS];

/** Common problem categories for create-ticket form. */
export const HYBRID_TICKET_TYPES = [
	"Deposit",
	"Withdraw",
	"Account",
	"Game",
	"Bonus",
	"Other",
] as const;

export type HybridTicketType = (typeof HYBRID_TICKET_TYPES)[number];

export interface HybridPlayerTicket {
	ticket_id: number;
	ticket_type: HybridTicketType | string;
	description: string;
	status: HybridTicketStatus | string;
	created_at: string;
	resolved_at: string | null;
}

export interface HybridCreateTicketPayload {
	ticket_type: string;
	description: string;
}

export interface HybridCloseTicketPayload {
	ticket_id: number;
}

export const canPlayerCloseHybridTicket = (
	status: HybridTicketStatus | string | undefined
): boolean => {
	const normalized = String(status ?? "")
		.trim()
		.toUpperCase();
	return normalized === HYBRID_TICKET_STATUS.PENDING;
};

const firstDefined = <T>(...values: T[]): T | undefined =>
	values.find((value) => value !== undefined && value !== null);

const toTicketDate = (value: unknown): string => {
	if (value == null || value === "") return "";
	if (typeof value === "string") return value;
	return String(value);
};

/** Normalize ticket rows from `hybridGetTickets` (array or `{ records }`). */
export const parseHybridTickets = (data: any): HybridPlayerTicket[] => {
	const rows: any[] = Array.isArray(data)
		? data
		: Array.isArray(data?.records)
			? data.records
			: Array.isArray(data?.tickets)
				? data.tickets
				: [];

	return rows
		.map((row) => {
			const ticketId = Number(
				firstDefined(row?.ticket_id, row?.hybrid_player_ticket_id, row?.id)
			);
			if (!Number.isFinite(ticketId) || ticketId <= 0) {
				return null;
			}

			const resolvedRaw = firstDefined(
				row?.resolved_at,
				row?.hybrid_player_ticket_resolved_dt,
				row?.closed_at,
				row?.completed_datetime
			);

			return {
				ticket_id: ticketId,
				ticket_type: String(
					firstDefined(
						row?.ticket_type,
						row?.hybrid_player_ticket_type,
						row?.type
					) ?? ""
				),
				description: String(
					firstDefined(
						row?.description,
						row?.hybrid_player_ticket_description,
						row?.ticket_description
					) ?? ""
				),
				status: String(
					firstDefined(
						row?.status,
						row?.hybrid_player_ticket_status,
						row?.ticket_status
					) ?? ""
				),
				created_at: toTicketDate(
					firstDefined(
						row?.created_at,
						row?.hybrid_player_ticket_created_dt,
						row?.created_dt,
						row?.created_datetime
					)
				),
				resolved_at:
					resolvedRaw == null || resolvedRaw === ""
						? null
						: toTicketDate(resolvedRaw),
			} as HybridPlayerTicket;
		})
		.filter((ticket): ticket is HybridPlayerTicket => ticket != null);
};

/** POST `/v1/private/hybridPlayer/hybridGetTickets` */
export const hybridGetTickets = (): Promise<ResponseData<any>> => {
	return apiCaller(`${baseUrl}/hybridGetTickets`, {});
};

/** POST `/v1/private/hybridPlayer/hybridCreateTicket` */
export const hybridCreateTicket = (
	payload: HybridCreateTicketPayload
): Promise<ResponseData<any>> => {
	return apiCaller(`${baseUrl}/hybridCreateTicket`, {
		ticket_type: payload.ticket_type.trim(),
		description: payload.description.trim(),
	});
};

/** POST `/v1/private/hybridPlayer/hybridCloseTicket` */
export const hybridCloseTicket = (
	payload: HybridCloseTicketPayload
): Promise<ResponseData<any>> => {
	return apiCaller(`${baseUrl}/hybridCloseTicket`, {
		ticket_id: payload.ticket_id,
	});
};

export interface EKYCSupportDocOption {
	ekyc_support_doc_id: number;
	ekyc_support_doc_un_code: string;
	ekyc_support_doc_country_name: string;
	ekyc_support_doc_type: string;
	ekyc_support_doc_type_id: string;
}

export interface GenerateEKYCRequestUrlPayload {
	metaInfo: string | Record<string, unknown>;
	docType: string;
}

export interface GenerateEKYCRequestUrlResult {
	requestId?: string;
	transactionId?: string;
	transactionUrl?: string;
	ekyc_transaction_id?: number;
}

export interface EKYCStatusResult {
	in_progress?: boolean;
	ekyc_transaction_status?: string | null;
}

/** POST `/v1/private/hybridPlayer/getEKYCSupportDocTypes` */
export const getEKYCSupportDocTypes = (payload?: { un_code?: string }) => {
	return apiCaller(`${baseUrl}/getEKYCSupportDocTypes`, payload || {});
};

/** POST `/v1/private/hybridPlayer/getEKYCStatus` */
export const getEKYCStatus = () => {
	return apiCaller(`${baseUrl}/getEKYCStatus`, {});
};

/** POST `/v1/private/hybridPlayer/generateEKYCRequestUrl` */
export const generateEKYCRequestUrl = (
	payload: GenerateEKYCRequestUrlPayload
) => {
	return apiCaller(`${baseUrl}/generateEKYCRequestUrl`, {
		metaInfo: payload.metaInfo,
		docType: String(payload.docType ?? "").trim(),
	});
};

export const hybridGetDownlineHierarchyByLevel = (payload: {
	display_downline_level?: number;
}): Promise<ResponseData<{ message: string | null }>> => {
	return apiCaller(`${baseUrl}/hybridGetDownlineHierarchyByLevel `, {
		display_downline_level: payload.display_downline_level,
	});
};

export type HybridDownlinePlayer = {
	hybrid_player_id: number;
	hybrid_player_username: string;
	hybrid_player_phone_no: string | null;
	hybrid_player_status: "ACTIVE" | "INACTIVE" | string;
	level: number;
	parent_id: number | null;
};
